import React, { useEffect, useState } from "react"
import { toast } from "react-hot-toast"
import { MdOutlineDelete, MdEdit } from "react-icons/md"
import { IoMdAdd } from "react-icons/io"
import { AiOutlineClose } from "react-icons/ai"
import Heading from "../../components/Heading"
import DeleteConfirm from "../../components/DeleteConfirm"
import axiosWrapper from "../../utils/AxiosWrapper"
import CustomButton from "../../components/CustomButton"
import Loading from "../../components/Loading"

const MethodPaperSubSubjects = () => {
  const userToken = localStorage.getItem("userToken")
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [branches, setBranches] = useState([])
  const [filters, setFilters] = useState({ branch: "", semester: "" })
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleteId, setDeleteId] = useState(null)
  const [form, setForm] = useState({
    name: "",
    description: "",
    branch: "",
    semester: "",
    isActive: true,
  })

  const loadBranches = async () => {
    try {
      const res = await axiosWrapper.get(`/branch`, {
        headers: { Authorization: `Bearer ${userToken}` },
      })
      if (res.data.success) setBranches(res.data.data || [])
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(e.response?.data?.message || "Failed to load branches")
      }
    }
  }

  const loadRows = async () => {
    try {
      setLoading(true)
      const q = new URLSearchParams()
      if (filters.branch) q.append("branch", filters.branch)
      if (filters.semester) q.append("semester", filters.semester)
      const res = await axiosWrapper.get(
        `/method-paper-sub-subjects${q.toString() ? `?${q}` : ""}`,
        { headers: { Authorization: `Bearer ${userToken}` } }
      )
      if (res.data.success) setRows(res.data.data || [])
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load")
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBranches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.branch, filters.semester])

  const reset = () => {
    setForm({
      name: "",
      description: "",
      branch: "",
      semester: "",
      isActive: true,
    })
    setEditingId(null)
    setShowModal(false)
  }

  const submit = async () => {
    if (!form.name || !form.branch || !form.semester) {
      toast.error("Name, branch and semester are required")
      return
    }
    try {
      setLoading(true)
      const headers = { Authorization: `Bearer ${userToken}` }
      const body = {
        name: form.name,
        description: form.description,
        branch: form.branch,
        semester: Number(form.semester),
        isActive: form.isActive,
      }
      const res = editingId
        ? await axiosWrapper.patch(
            `/method-paper-sub-subjects/${editingId}`,
            body,
            { headers }
          )
        : await axiosWrapper.post(`/method-paper-sub-subjects`, body, {
            headers,
          })
      if (res.data.success) {
        toast.success(res.data.message)
        reset()
        loadRows()
      } else {
        toast.error(res.data.message)
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed")
    } finally {
      setLoading(false)
    }
  }

  const openEdit = (r) => {
    setEditingId(r._id)
    setForm({
      name: r.name,
      description: r.description || "",
      branch: r.branch?._id || r.branch,
      semester: r.semester,
      isActive: r.isActive !== false,
    })
    setShowModal(true)
  }

  const confirmDelete = async () => {
    try {
      setLoading(true)
      const res = await axiosWrapper.delete(
        `/method-paper-sub-subjects/${deleteId}`,
        { headers: { Authorization: `Bearer ${userToken}` } }
      )
      if (res.data.success) {
        toast.success(res.data.message)
        setDeleteId(null)
        loadRows()
      } else {
        toast.error(res.data.message)
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full mx-auto mt-10 flex flex-col mb-10">
      <div className="flex justify-between items-center w-full flex-wrap gap-4">
        <Heading title="Method paper sub-subjects" />
        <CustomButton onClick={() => { reset(); setShowModal(true) }}>
          <IoMdAdd className="text-2xl" />
        </CustomButton>
      </div>
      <p className="text-gray-600 text-sm mt-2 max-w-2xl">
        These are separate from regular subjects. Students pick one on their
        profile for method paper (scoped by branch and semester).
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 w-full max-w-3xl">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter branch
          </label>
          <select
            className="w-full px-4 py-2 border rounded-md"
            value={filters.branch}
            onChange={(e) =>
              setFilters((f) => ({ ...f, branch: e.target.value }))
            }
          >
            <option value="">All</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter semester
          </label>
          <select
            className="w-full px-4 py-2 border rounded-md"
            value={filters.semester}
            onChange={(e) =>
              setFilters((f) => ({ ...f, semester: e.target.value }))
            }
          >
            <option value="">All</option>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <Loading />}

      <div className="mt-8 overflow-x-auto w-full">
        <table className="text-sm min-w-full bg-white">
          <thead>
            <tr className="bg-blue-500 text-white">
              <th className="py-3 px-4 text-left">Name</th>
              <th className="py-3 px-4 text-left">Branch</th>
              <th className="py-3 px-4 text-left">Sem</th>
              <th className="py-3 px-4 text-left">Active</th>
              <th className="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-8 text-gray-500">
                  No sub-subjects yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r._id} className="border-b hover:bg-blue-50">
                  <td className="py-3 px-4">{r.name}</td>
                  <td className="py-3 px-4">{r.branch?.name}</td>
                  <td className="py-3 px-4">{r.semester}</td>
                  <td className="py-3 px-4">{r.isActive ? "Yes" : "No"}</td>
                  <td className="py-3 px-4 text-center flex justify-center gap-2">
                    <CustomButton
                      variant="secondary"
                      className="!p-2"
                      onClick={() => openEdit(r)}
                    >
                      <MdEdit />
                    </CustomButton>
                    <CustomButton
                      variant="danger"
                      className="!p-2"
                      onClick={() => setDeleteId(r._id)}
                    >
                      <MdOutlineDelete />
                    </CustomButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingId ? "Edit sub-subject" : "Add sub-subject"}
              </h2>
              <CustomButton variant="secondary" onClick={reset}>
                <AiOutlineClose size={22} />
              </CustomButton>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  className="w-full px-4 py-2 border rounded-md"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  className="w-full px-4 py-2 border rounded-md"
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Branch</label>
                <select
                  className="w-full px-4 py-2 border rounded-md"
                  value={form.branch}
                  onChange={(e) => setForm({ ...form, branch: e.target.value })}
                >
                  <option value="">Select</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Semester
                </label>
                <select
                  className="w-full px-4 py-2 border rounded-md"
                  value={form.semester}
                  onChange={(e) =>
                    setForm({ ...form, semester: e.target.value })
                  }
                >
                  <option value="">Select</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />
                Active (shown to students)
              </label>
              <div className="flex justify-end gap-2 pt-4">
                <CustomButton variant="secondary" onClick={reset}>
                  Cancel
                </CustomButton>
                <CustomButton onClick={submit} disabled={loading}>
                  Save
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirm
        isOpen={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        message="Delete this sub-subject? (Only if no student has selected it.)"
      />
    </div>
  )
}

export default MethodPaperSubSubjects
